import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { identity } from '@repletoryreach/sdk-api';
import { Button, Card, Group, Skeleton, Stack, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { disablePush } from '../../../../lib/push';

/**
 * Where this account is signed in.
 *
 * `settings-security.md` §4 items 8 and 9: the page has a section that
 * PROMISES a sessions list and there was no endpoint behind it, so the only
 * session control was "sign out everywhere". Noticing one suspicious device
 * meant signing yourself out too, and there was no way to see the device at
 * all. `GET /v1/auth/sessions` and `DELETE /v1/auth/sessions/{id}` were added
 * over data `auth_refresh_tokens` has always recorded.
 */
export function SessionsPanel() {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isPending, error } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => identity.listSessions()
  });

  const revoke = useMutation({
    mutationFn: (id: string) => identity.revokeSession(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sessions'] });
      notifySuccess(t('settings.security.sessions.toast.ended'), t('settings.nav.security'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.security'))
  });

  const signOutAll = useMutation({
    mutationFn: async () => { await disablePush(); return identity.logout(); },
    onSuccess: () => {
      navigate('/auth/login', { replace: true });
      window.location.reload();
    },
    /*
     * §4 item 5: this used to `catch { }` and then navigate and reload
     * regardless, so a failed revoke left the user believing every session was
     * dead. For a security control, silently claiming success is the wrong
     * default.
     */
    onError: (e) => notifyError(
      e instanceof Error ? e.message : t('settings.security.sessions.signOutAllFailed'),
      t('settings.nav.security')
    )
  });

  const sessions = data?.data ?? [];

  return (
    <Card withBorder data-guide="security-sessions">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Title order={4}>{t('settings.security.sessions.title')}</Title>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.security.sessions.hint')}</Text>
          </Stack>
          <Button
            size="xs"
            variant="default"
            color="red"
            loading={signOutAll.isPending}
            onClick={() => openConfirmModal({
              title: t('settings.security.modal.signOutAll.title'),
              children: (
                <Stack gap={8}>
                  <Text role="bodySmall">{t('settings.security.modal.signOutAll.body')}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('settings.security.modal.signOutAll.includesYou')}
                  </Text>
                </Stack>
              ),
              labels: { confirm: t('settings.security.actions.signOutAll'), cancel: t('action.cancel') },
              confirmProps: { color: 'red' },
              onConfirm: () => signOutAll.mutate()
            })}
          >
            {t('settings.security.actions.signOutAll')}
          </Button>
        </Group>

        {error && <Alert color="error" role="alert">{t('settings.security.sessions.error')}</Alert>}

        {isPending && <Stack gap="xs">{[0, 1].map((i) => <Skeleton key={i} height={52} />)}</Stack>}

        {!isPending && !error && sessions.length === 0 && (
          <EmptyState title={t('settings.security.sessions.empty')} />
        )}

        {sessions.map((s) => (
          <Group key={s.id} justify="space-between" wrap="nowrap" className="rr-security__session">
            <Stack gap={1} style={{ minWidth: 0 }}>
              <Text role="bodySmall">{describeAgent(s.user_agent, t)}</Text>
              <Tooltip label={formatDateTime(s.created_at)} position="top">
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {t('settings.security.sessions.startedAt', { when: formatRelative(s.created_at) })}
                </Text>
              </Tooltip>
            </Stack>
            <Group gap="xs" wrap="nowrap">
              {!s.user_agent && <Badge size="xs" variant="light">{t('settings.security.sessions.unknownDevice')}</Badge>}
              <Button
                size="xs"
                variant="subtle"
                color="red"
                loading={revoke.isPending && revoke.variables === s.id}
                onClick={() => revoke.mutate(s.id)}
              >
                {t('settings.security.sessions.end')}
              </Button>
            </Group>
          </Group>
        ))}
      </Stack>
    </Card>
  );
}

/**
 * A user agent, in words.
 *
 * The raw string is a wall of version numbers; what someone checking their
 * sessions needs is "Chrome on a Mac". Anything unrecognised falls back to the
 * raw value rather than to a wrong guess.
 */
function describeAgent(
  agent: string | null | undefined,
  t: (k: string, v?: Record<string, string | number>) => string
): string {
  if (!agent) return t('settings.security.sessions.unknownDevice');

  // i18n-ignore: browser and platform names are proper nouns; translating
  // them would be wrong in every language.
  const browser = /Edg\//.test(agent) ? 'Edge'        // i18n-ignore: product name
    : /OPR\//.test(agent) ? 'Opera'                    // i18n-ignore: product name
    : /Chrome\//.test(agent) ? 'Chrome'      // i18n-ignore: product name
    : /Firefox\//.test(agent) ? 'Firefox'    // i18n-ignore: product name
    : /Safari\//.test(agent) ? 'Safari'      // i18n-ignore: product name
    : null;

  const platform = /iPhone|iPad/.test(agent) ? 'iOS'
    : /Android/.test(agent) ? 'Android'      // i18n-ignore: product name
    : /Mac OS X/.test(agent) ? 'macOS'
    : /Windows/.test(agent) ? 'Windows'      // i18n-ignore: product name
    : /Linux/.test(agent) ? 'Linux'          // i18n-ignore: product name
    : null;

  if (browser && platform) return t('settings.security.sessions.device', { browser, platform });
  return browser ?? platform ?? agent;
}
